import { useMemo } from 'react';
import { useEmployees, useTeams } from '@/shared/hooks';
import {
  filterValidAssignees,
  getFullName,
} from '@/shared/utils/employee-utils';
import { PersonAvatar, TeamIcon, SelectedCheckmark } from './PeopleTeamAvatars';

export interface PeopleTeamItem {
  type: 'team' | 'person';
  id: string;
  name: string;
  initials: string;
  imageUrl?: string;
}

interface PeopleTeamListProps {
  query?: string;
  teamsLabel?: string;
  peopleLabel?: string;
  noResultsLabel?: string;
  highlightedIndex?: number;
  selectedTeamIds?: string[];
  selectedUserIds?: string[];
  onSelect: (item: PeopleTeamItem) => void;
  onMouseEnterItem?: (index: number) => void;
  preventFocusSteal?: boolean;
}

export const usePeopleTeamData = () => {
  const { data: employees = [] } = useEmployees({
    isActive: true,
    isDeleted: false,
  });

  const { data: teams = [] } = useTeams({
    isDeleted: false,
  });

  const teamItems: PeopleTeamItem[] = useMemo(
    () =>
      teams.map((team) => ({
        type: 'team' as const,
        id: team._id,
        name: team.name,
        initials: '',
      })),
    [teams]
  );

  const personItems: PeopleTeamItem[] = useMemo(() => {
    const valid = filterValidAssignees(employees.filter((emp) => emp.isActive));
    return valid.map((emp) => ({
      type: 'person' as const,
      id: emp._id,
      name: getFullName(emp) || '',
      initials: `${emp.personalInfo.firstName?.[0] || ''}${
        emp.personalInfo.lastName?.[0] || ''
      }`.toUpperCase(),
      imageUrl: emp.personalInfo.imageUrl,
    }));
  }, [employees]);

  return { teamItems, personItems };
};

export const filterPeopleTeamItems = (
  items: PeopleTeamItem[],
  query: string
): PeopleTeamItem[] => {
  const lowerQuery = query.toLowerCase();

  return items.filter((item) => item.name.toLowerCase().includes(lowerQuery));
};

export const PeopleTeamList = ({
  query = '',
  teamsLabel = 'Teams',
  peopleLabel = 'People',
  noResultsLabel = 'No results found',
  highlightedIndex,
  selectedTeamIds,
  selectedUserIds,
  onSelect,
  onMouseEnterItem,
  preventFocusSteal = false,
}: PeopleTeamListProps) => {
  const { teamItems, personItems } = usePeopleTeamData();

  const filteredTeams = useMemo(
    () => filterPeopleTeamItems(teamItems, query),
    [teamItems, query]
  );

  const filteredPeople = useMemo(
    () => filterPeopleTeamItems(personItems, query),
    [personItems, query]
  );

  const allItems = useMemo(
    () => [...filteredTeams, ...filteredPeople],
    [filteredTeams, filteredPeople]
  );

  if (allItems.length === 0) {
    return (
      <div className="px-2 py-3 text-[13px] text-muted-foreground text-center">
        {noResultsLabel}
      </div>
    );
  }

  const handleClick = (item: PeopleTeamItem, e: React.MouseEvent) => {
    if (preventFocusSteal) e.preventDefault();
    onSelect(item);
  };

  return (
    <>
      {filteredTeams.length > 0 && (
        <>
          <div className="text-[11px] font-semibold tracking-[0.04em] uppercase text-muted-foreground px-2 pt-2 pb-1">
            {teamsLabel}
          </div>
          {filteredTeams.map((item) => {
            const idx = allItems.indexOf(item);
            const isHighlighted = highlightedIndex === idx;
            const isSelected = selectedTeamIds?.includes(item.id);
            return (
              <button
                key={item.id}
                type="button"
                className={`flex items-center gap-2 w-full px-2 py-1.5 rounded text-[13px] text-foreground transition-colors cursor-pointer text-start ${
                  isHighlighted ? 'bg-muted' : 'hover:bg-muted'
                }`}
                onMouseDown={(e) => handleClick(item, e)}
                onMouseEnter={() => onMouseEnterItem?.(idx)}
              >
                <TeamIcon />
                {item.name}
                {isSelected && <SelectedCheckmark />}
              </button>
            );
          })}
        </>
      )}

      {filteredPeople.length > 0 && (
        <>
          <div className="text-[11px] font-semibold tracking-[0.04em] uppercase text-muted-foreground px-2 pt-2 pb-1">
            {peopleLabel}
          </div>
          {filteredPeople.map((item) => {
            const idx = allItems.indexOf(item);
            const isHighlighted = highlightedIndex === idx;
            const isSelected = selectedUserIds?.includes(item.id);
            return (
              <button
                key={item.id}
                type="button"
                className={`flex items-center gap-2 w-full px-2 py-1.5 rounded text-[13px] text-foreground transition-colors cursor-pointer text-start ${
                  isHighlighted ? 'bg-muted' : 'hover:bg-muted'
                }`}
                onMouseDown={(e) => handleClick(item, e)}
                onMouseEnter={() => onMouseEnterItem?.(idx)}
              >
                <PersonAvatar
                  initials={item.initials}
                  imageUrl={item.imageUrl}
                />
                {item.name}
                {isSelected && <SelectedCheckmark />}
              </button>
            );
          })}
        </>
      )}
    </>
  );
};

/** Returns the flat list of filtered items for keyboard navigation */
export const usePeopleTeamFilteredList = (query: string) => {
  const { teamItems, personItems } = usePeopleTeamData();

  return useMemo(() => {
    const teams = filterPeopleTeamItems(teamItems, query);
    const people = filterPeopleTeamItems(personItems, query);
    return [...teams, ...people];
  }, [teamItems, personItems, query]);
};
